import { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Switch, Alert, Space, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const STATUS_OPTIONS = [
  { value: 'keep', label: 'No cambiar' },
  { value: 'open', label: 'Abierto' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'resolved', label: 'Resuelto' },
  { value: 'discarded', label: 'Descartado' },
];

/**
 * Modal para responder a una incidencia desde el drawer de gestión.
 * - TextArea con el mensaje (max 5000)
 * - Toggle "Notificar al reportero por email" (default on)
 * - Select opcional "Cambiar estado de la incidencia"
 *
 * Deshabilita el toggle de notificación si el reportero no tiene email
 * conocido (queda forzado en off).
 */
const ReplyIncidentModal = ({ open, incident, reporter, submitting, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  const [notify, setNotify] = useState(true);

  const hasEmail = Boolean(reporter?.email);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setNotify(hasEmail);
    }
  }, [open, hasEmail, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const newStatus = values.newStatus && values.newStatus !== 'keep' ? values.newStatus : null;
      await onSubmit?.({
        message: values.message,
        notify: hasEmail ? notify : false,
        newStatus,
      });
    } catch (err) {
      if (err?.errorFields) return;
      throw err;
    }
  };

  return (
    <Modal
      title={incident ? `Responder a incidencia #${incident.id}` : 'Responder'}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Enviar respuesta"
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={560}
      destroyOnHidden
      maskClosable={!submitting}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {reporter ? (
          <Alert
            type={hasEmail ? 'info' : 'warning'}
            showIcon
            message={
              hasEmail ? (
                <>
                  Se enviará un email a <Typography.Text strong>{reporter.email}</Typography.Text>
                  {reporter.fullname ? <> ({reporter.fullname})</> : null}
                </>
              ) : (
                <>
                  El reportero no tiene email registrado — la respuesta quedará guardada pero no se
                  notificará.
                </>
              )
            }
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Reporte anónimo: la respuesta quedará guardada pero no se notificará."
          />
        )}

        <Form form={form} layout="vertical" disabled={submitting}>
          <Form.Item
            label="Tu respuesta"
            name="message"
            rules={[
              { required: true, message: 'Escribí una respuesta' },
              { max: 5000, message: 'Máximo 5000 caracteres' },
            ]}
          >
            <TextArea
              rows={6}
              maxLength={5000}
              showCount
              placeholder="Hola, recibimos tu reporte y queremos contarte que…"
            />
          </Form.Item>

          <Form.Item label="Cambiar estado de la incidencia" name="newStatus" initialValue="keep">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>

          <Form.Item label="Notificar al reportero por email">
            <Switch
              checked={hasEmail ? notify : false}
              disabled={!hasEmail}
              onChange={setNotify}
              checkedChildren={<MailOutlined />}
            />
            <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              {hasEmail
                ? notify
                  ? 'El reportero recibirá un email con tu respuesta.'
                  : 'La respuesta quedará guardada pero no se enviará email.'
                : 'Deshabilitado por falta de email del reportero.'}
            </Typography.Text>
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};

export default ReplyIncidentModal;
